/**
 * The control-plane wire contract between the loader and the Quarterzip frames.
 */

export const PROTOCOL_VERSION = 1;

export const SUPPORTED_PROTOCOL_VERSIONS = [1] as const;

export type ProtocolVersion = (typeof SUPPORTED_PROTOCOL_VERSIONS)[number];

export const oldestVersion = (versions: readonly number[]): number => Math.min(...versions);

/**
 * The version `qz:ready` is sent in. It precedes any loader message, so nothing has been negotiated
 * yet and only the oldest supported version is guaranteed parseable by the receiving loader.
 */
export const HANDSHAKE_PROTOCOL_VERSION: ProtocolVersion = oldestVersion(
	SUPPORTED_PROTOCOL_VERSIONS
) as ProtocolVersion;

export type FrameRole = 'call' | 'pip';

export interface CallUser {
	id: string;
	email?: string;
	displayName?: string;
}

export interface CallOpenPayload {
	agentId: string;
	workspaceToken: string;
	user: CallUser;
	context?: string;
	locale?: string;
}

/** Reason for call end. Possible values are available on {@link https://docs.quarterzip.ai}. */
export type CallEndReason = string;
/** Code for call error. Possible values are available on {@link https://docs.quarterzip.ai}. */
export type CallErrorCode = string;

export type ControlMessage =
	// frame -> loader: mounted and listening. Nothing may be sent to a frame before this arrives.
	| { v: ProtocolVersion; kind: 'qz:ready'; role: FrameRole }
	// frame -> loader: the panel's own geometry and easing. The loader keeps no copy of any of it.
	| {
			v: ProtocolVersion;
			kind: 'qz:resize';
			width: number;
			height: number;
			radius: number;
			durationMs: number;
	  }
	// frame -> loader: themed and safe to show. Not qz:ready — the theme needs a config fetch that
	// qz:ready is what triggers. The loader holds its puck until this arrives.
	| { v: ProtocolVersion; kind: 'qz:reveal' }
	// frame -> loader: a drag began on the handle; the loader pins the box to anchor the deltas. The
	// engine keeps the gesture in the frame that received the pointerdown, so the frame owns it.
	| { v: ProtocolVersion; kind: 'qz:drag'; phase: 'start' }
	// frame -> loader: displacement since the grab in CSS pixels, measured INSIDE the frame. Absolute,
	// not incremental, so a dropped message costs nothing.
	| { v: ProtocolVersion; kind: 'qz:drag'; phase: 'move'; dx: number; dy: number }
	// frame -> loader: the gesture ended.
	| { v: ProtocolVersion; kind: 'qz:drag'; phase: 'end' }
	// frame -> loader: open or close the Document PiP window. Only a top-level traversable may call
	// requestWindow(), and only the frame receives the gesture-free enterpictureinpicture action. The
	// loader cannot substitute a visibilitychange listener: that is not user activation, so
	// requestWindow() would be refused.
	| { v: ProtocolVersion; kind: 'qz:pip'; open: boolean }
	// frame -> loader: the call is live — transport connected, bot joined.
	| { v: ProtocolVersion; kind: 'qz:call-started' }
	// frame -> loader: the call conversation ended. The session may still be alive (feedback screen).
	| { v: ProtocolVersion; kind: 'qz:call-ended' }
	// frame -> loader: the session is over and will not restart. Terminal.
	| { v: ProtocolVersion; kind: 'qz:end'; reason: CallEndReason }
	// frame -> loader: a call-level failure. `fatal` states whether the call can continue and is read
	// off the wire, never inferred by the loader.
	| { v: ProtocolVersion; kind: 'qz:error'; code: CallErrorCode; fatal: boolean }
	// loader -> call: relays the integrator's open() arguments.
	| { v: ProtocolVersion; kind: 'qz:open'; args: CallOpenPayload }
	// loader -> call: relays the integrator's close(). The frame winds down and answers with qz:end.
	| { v: ProtocolVersion; kind: 'qz:close' }
	// loader -> both: one end of a fresh MessageChannel. Sent to a frame only AFTER that frame's qz:ready.
	| { v: ProtocolVersion; kind: 'qz:port' }
	// loader -> call: the PiP window closed.
	| { v: ProtocolVersion; kind: 'qz:pip-closed'; userInitiated: boolean };

/**
 * The subset the loader is allowed to send. Narrows the loader's post() so a frame->loader message
 * cannot be posted in the wrong direction by mistake.
 */
export type ToFrameMessage = Extract<
	ControlMessage,
	{ kind: 'qz:open' | 'qz:close' | 'qz:port' | 'qz:pip-closed' }
>;

const hasKind = (data: unknown): data is { v: unknown; kind: unknown } =>
	typeof data === 'object' && data !== null && 'kind' in data && 'v' in data;

/**
 * Whether a version is one this side still accepts. Takes the supported SET, not the current
 * version: accepting a range is what lets a frame and a loader of different ages talk to each other,
 * so the membership test has to survive the set growing past one entry.
 */
export const isSupportedVersion = (
	v: unknown,
	supported: readonly number[] = SUPPORTED_PROTOCOL_VERSIONS
): v is ProtocolVersion => typeof v === 'number' && supported.includes(v);

/** Validates control message. Accepts any version this frame still speaks, not just the newest. */
export const isControlMessage = (
	data: unknown,
	supported: readonly number[] = SUPPORTED_PROTOCOL_VERSIONS
): data is ControlMessage =>
	hasKind(data) &&
	isSupportedVersion(data.v, supported) &&
	typeof data.kind === 'string' &&
	data.kind.startsWith('qz:');

/**
 * Validates frame message. `qz:ready` is checked against the handshake version rather than the spoken
 * one, because it arrives before anything is negotiated.
 *
 * Both versions are parameters so the two can diverge without touching this logic.
 */
export const isFromFrameMessage = (
	data: unknown,
	spoken: number = PROTOCOL_VERSION,
	handshake: number = HANDSHAKE_PROTOCOL_VERSION
): data is ControlMessage =>
	hasKind(data) &&
	typeof data.kind === 'string' &&
	data.kind.startsWith('qz:') &&
	(data.v === spoken || (data.kind === 'qz:ready' && data.v === handshake));
